import { getDepositTicketAllBets, transactionsSearch, getClerkPayouts } from '@/utils/api';
import { getField, updateField } from 'vuex-map-fields';
import { formatOpenBetsData } from '@/views/history/history-utils';
import i18n from '@/utils/i18n';
import { _api } from '@/utils/apiAxios';

import { extractError } from '@/utils/errorUtil';
import Vue from 'vue';
import { printDepositTicket } from '@/plugins/printFactory';
import { roundAmount } from '@/views/sports/components/bet-slip/bet-slip-utils';

const getDefaultState = () => {
  return {
    scanCode: '',
    amount: '',
    state: 'EMPTY', // EMPTY | DETAIL
    ticketAllBets: {
      bets: [],
      horse_bets: [],
    },
    ticketAllBetsStatus: {
      loading: false,
      success: false,
      error: false,
    },
    transactions: [],
    transactionsStatus: {
      loading: false,
      success: false,
      error: false,
    },
    balanceStatus: {
      loading: false,
      success: false,
      error: false,
    },
    balanceResponse: {},
    invalidScanCode: '',
    openWagerStatusModal: false,
    isMobileScanCode: false,
  };
};

const state = getDefaultState();

const getters = {
  getField,
  transactionsTableFields: _ => [
    {
      key: 'ticket_number',
      label: i18n.t('ClerkDeposit.TicketNumber'),
      _searchable: true,
      _sortable: true,
    },
    {
      key: 'clerk.clerk_user',
      label: i18n.t('ClerkDeposit.Clerk'),
      _searchable: true,
      _sortable: false,
    },
    {
      key: 'transaction_type',
      label: i18n.t('Type'),
      _searchable: false,
      _sortable: false,
    },
    {
      key: 'last_modification',
      label: i18n.t('ClerkDeposit.LastModify'),
      _searchable: true,
      _sortable: true,
    },
    {
      key: 'amount',
      label: i18n.t('Amount'),
      _searchable: true,
      _sortable: true,
    },
  ],
  betsFields: _ => [
    {
      key: 'PlacedDate',
      label: i18n.t('Placed'),
      _searchable: true,
      _sortable: true,
      thStyle: { width: '170px' },
      _sortDirection: 'desc',
    },
    {
      key: 'TicketNumber',
      label: i18n.t('BetSlip.Bet Ticket'),
      _searchable: true,
      _sortable: true,
    },
    {
      key: 'CompleteDescription',
      label: i18n.t('Type'),
      _searchable: true,
      _sortable: false,
    },
    {
      key: 'Graded',
      label: i18n.t('Graded'),
      _searchable: false,
      _sortable: false,
    },
    {
      key: 'Status',
      label: i18n.t('Status'),
      _searchable: false,
      _sortable: false,
    },
    {
      key: 'OriginalRiskAmount',
      label: i18n.t('Risk'),
      _searchable: true,
      _sortable: true,
    },
    {
      key: 'OriginalWinAmount',
      label: i18n.t('Win'),
      _searchable: true,
      _sortable: true,
    },
  ],
};

const mutations = {
  updateField,
  resetState(state) {
    Object.assign(state, getDefaultState());
  },
  setState(state, val) {
    state.state = val;
  },
  setTicketAllBets(state, response) {
    state.ticketAllBets = response;
  },
  setTicketAllBetsStatus(state, row) {
    state.ticketAllBetsStatus = {
      ...state.ticketAllBetsStatus,
      ...row,
    };
  },
  setTransactions(state, response) {
    state.transactions = response;
  },
  setTransactionsSearchStatus(state, row) {
    state.transactionsStatus = {
      ...state.transactionsStatus,
      ...row,
    };
  },
  setBalanceStatus(state, row) {
    state.balanceStatus = {
      ...state.balanceStatus,
      ...row,
    };
  },
  setBalanceResponse(state, response) {
    state.balanceResponse = response;
  },
  setInvalidScanCode(state, row) {
    state.invalidScanCode = row;
  },
  setScanCode(state, scanCode) {
    state.scanCode = scanCode;
  },
  setOpenWagerStatusModal(state, value) {
    state.openWagerStatusModal = value;
  },
  setIsMobileScanCode(state, value) {
    state.isMobileScanCode = value;
  },
};

const actions = {
  tryScanCode({ dispatch, commit, rootState }) {
    const scanCode = state.scanCode.trim();
    if (!scanCode) return;
    commit('setInvalidScanCode', '');
    commit('setTransactions', []);
    commit('setTicketAllBets', []);
    commit('setTicketAllBetsStatus', { loading: true });
    commit('setBalanceStatus', { loading: true, success: false, error: false });
    getClerkPayouts({ scanCode }).then(r => {
      if (r.data.IsSuccess) {
        if (scanCode.charAt(0) === 'W') {
          commit('setIsMobileScanCode', true);
        } else {
          commit('setIsMobileScanCode', false);
        }
        const innerResponse = r.data.Data;
        const failed = innerResponse.status !== 'ok';
        console.log(innerResponse);
        if (!failed) {
          const data = innerResponse.data;
          commit('setBalanceResponse', data);
          commit('setBalanceStatus', { success: true, error: false });
          commit('setState', 'DETAIL');
          commit('setTicketAllBets', {
            bets: innerResponse.data.bets,
            horse_bets: innerResponse.data.horse_bets,
          });
          commit('setTicketAllBetsStatus', {
            loading: false,
            success: true,
            error: false,
          });
          // dispatch("tryGetDepositTicketAllBets");
          // dispatch("tryTransactionsSearch");
          const betData = innerResponse.data;
          const isEmpty = (field, isObject = false) =>
            isObject ? !Object.keys(betData[field]).length : !betData[field];

          if (
            isEmpty('ticket_number') &&
            isEmpty('ticket_description') &&
            isEmpty('service_description') &&
            isEmpty('sold_date') &&
            isEmpty('sold_value') &&
            isEmpty('clerk_user') &&
            isEmpty('clerk_id') &&
            isEmpty('description') &&
            isEmpty('currency') &&
            isEmpty('exchange_rate') &&
            isEmpty('scan_code') &&
            isEmpty('status') &&
            isEmpty('receipts') &&
            isEmpty('amount_at_risk') &&
            isEmpty('settled_amount') &&
            isEmpty('current_balance') &&
            isEmpty('scan_type') &&
            isEmpty('bets', true) &&
            isEmpty('horse_bets', true)
          ) {
            if (scanCode.startsWith('B')) {
              dispatch('clerkWagerStore/tryScanWagerBarcode', scanCode, { root: true });
              commit('setOpenWagerStatusModal', true);
            }
          }
        } else {
          commit('setBalanceStatus', { success: false, error: true });
        }
      } else {
        commit('setBalanceStatus', { success: false, error: true });
        commit('setInvalidScanCode', true);
      }
      commit('setBalanceStatus', { loading: false });
      dispatch('settings/getClerkBalance', null, { root: true });
    });
  },
  tryGetDepositTicketAllBets({ commit, state, rootGetters }) {
    const scanCode = state.scanCode.trim();
    commit('setTicketAllBetsStatus', { loading: true });
    getDepositTicketAllBets({
      scan_code: scanCode,
    }).then(r => {
      if (r.data.IsSuccess) {
        const currencySymbol = rootGetters['player/currencySymbol'];
        commit('setTicketAllBets', formatOpenBetsData(r.data.Data.data, currencySymbol));
        commit('setTicketAllBetsStatus', {
          loading: false,
          success: true,
          error: false,
        });
      } else {
        commit('setTicketAllBetsStatus', {
          loading: false,
          success: false,
          error: true,
        });
      }
    });
  },
  tryTransactionsSearch({ commit, rootState, state }) {
    const scanCode = state.scanCode.trim();
    const formData = {
      mac_address: rootState.settings.macAddress,
      from: '2011-05-28 04:56:10.483',
      to: '2041-05-28 04:56:10.483',
      wallet_id: scanCode,
    };
    transactionsSearch(formData).then(r => {
      if (r.data.IsSuccess) {
        if (r.data.Data.status == 'ok') {
          commit('setTransactions', r.data.Data.data.data);
        } else {
          commit('setTransactionsSearchStatus', {
            success: false,
            error: true,
          });
        }
      } else {
        commit('setTransactionsSearchStatus', { success: false, error: true });
      }
      commit('setTransactionsSearchStatus', { loading: false });
    });
  },
  tryClerkWalletPayout({ commit, rootState, state, dispatch }) {
    const amount = state.amount;
    if (parseFloat(amount) > parseFloat(state.balanceResponse.available_balance)) {
      Vue.prototype.$modal.openErrorModal({
        title: 'Balance exceed',
      });
      return;
    }

    const formData = {
      balance_amount: state.amount,
      description: 'Deposited amount: ' + amount,
      mac_address: rootState.settings.macAddress,
      terminal_id: rootState.settings.terminalInfo.terminal_id,
      reference: 'Payout made ' + amount,
      scan_code: state.scanCode.trim(),
    };
    _api.post('/clerk/makepayout', formData).then(r => {
      if (r.data.IsSuccess) {
        Vue.prototype.$modal.openSuccessModal({
          title: 'Payout made successfully',
        });

        const innerData = r.data.Data.data;

        commit(
          'depositTicketStore/addCalc',
          {
            type: 'voucher',
            ticketNumber: innerData.payout.main_ticket_id,
            amount: -state.amount,
            minus: true,
          },
          {
            root: true,
          },
        );

        dispatch('tryScanCode');

        const ticketJson = {
          scan_code: null,
          amount: roundAmount(innerData.ticket.ticket_detail.cashed_value),
          ticket_number: innerData.ticket.ticket_detail.ticket_number,
          currency: rootState.settings.terminalInfo.currency_name,
          paid_date: innerData.ticket.ticket_detail.paid_date,
          paid_by: innerData.ticket.ticket_detail.paid_by,
        };

        printDepositTicket({
          clientType: rootState.settings.clientType,
          ticketJson,
          callback: () => {},
          dontShowDidIdPrintedModal: true,
        });
      } else {
        Vue.prototype.$modal.openErrorModal({
          title: extractError(r) || 'Error when payout',
        });
      }
      dispatch('settings/getClerkBalance', null, { root: true });
    });
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
